import { combineReducers } from 'redux';
import _ from 'lodash';
import {
  createApiReducer,
  chainReducers,
  INITIAL_API_STATE_ITEMS,
} from 'src/api';
import {
  FETCH_ACCOUNT_BACKGROUND_ASSESSMENTS_START,
  FETCH_ACCOUNT_BACKGROUND_ASSESSMENTS_SUCCESS,
  FETCH_ACCOUNT_BACKGROUND_ASSESSMENTS_FAILURE,
  FETCH_BACKGROUND_ASSESSMENT_START,
  FETCH_BACKGROUND_ASSESSMENT_SUCCESS,
  FETCH_BACKGROUND_ASSESSMENT_FAILURE,
  ADD_BACKGROUND_ASSESSMENT_START,
  ADD_BACKGROUND_ASSESSMENT_SUCCESS,
  ADD_BACKGROUND_ASSESSMENT_FAILURE,
  UPDATE_BACKGROUND_ASSESSMENT_START,
  UPDATE_BACKGROUND_ASSESSMENT_SUCCESS,
  UPDATE_BACKGROUND_ASSESSMENT_FAILURE,
  UPDATE_BACKGROUND_ASSESSMENTS_START,
  UPDATE_BACKGROUND_ASSESSMENTS_SUCCESS,
  UPDATE_BACKGROUND_ASSESSMENTS_FAILURE,
  FETCH_BACKGROUND_ASSESSMENT_TYPES_START,
  FETCH_BACKGROUND_ASSESSMENT_TYPES_SUCCESS,
  FETCH_BACKGROUND_ASSESSMENT_TYPES_FAILURE,
  FETCH_BACKGROUND_ASSESSMENT_TYPE_RESULTS_START,
  FETCH_BACKGROUND_ASSESSMENT_TYPE_RESULTS_SUCCESS,
  FETCH_BACKGROUND_ASSESSMENT_TYPE_RESULTS_FAILURE,
  FETCH_BACKGROUND_ASSESSMENT_LOGS_START,
  FETCH_BACKGROUND_ASSESSMENT_LOGS_SUCCESS,
  FETCH_BACKGROUND_ASSESSMENT_LOGS_FAILURE,
  ADD_BACKGROUND_ASSESSMENT_LOG_START,
  ADD_BACKGROUND_ASSESSMENT_LOG_SUCCESS,
  ADD_BACKGROUND_ASSESSMENT_LOG_FAILURE,
  FETCH_BACKGROUND_ASSESSMENT_LOG_TYPES_START,
  FETCH_BACKGROUND_ASSESSMENT_LOG_TYPES_SUCCESS,
  FETCH_BACKGROUND_ASSESSMENT_LOG_TYPES_FAILURE,
  FETCH_COMPLIANCE_HISTORY_START,
  FETCH_COMPLIANCE_HISTORY_SUCCESS,
  FETCH_COMPLIANCE_HISTORY_FAILURE,
  FETCH_COMPLIANCE_DASHBOARD_DATA_START,
  FETCH_COMPLIANCE_DASHBOARD_DATA_SUCCESS,
  FETCH_COMPLIANCE_DASHBOARD_DATA_FAILURE,
  FETCH_COMPLIANCE_SETTINGS_START,
  FETCH_COMPLIANCE_SETTINGS_SUCCESS,
  FETCH_COMPLIANCE_SETTINGS_FAILURE,
  UPDATE_COMPLIANCE_SETTINGS_START,
  UPDATE_COMPLIANCE_SETTINGS_SUCCESS,
  UPDATE_COMPLIANCE_SETTINGS_FAILURE,
  UPDATE_COMPLIANCE_SETTING_FAILURE,
  UPDATE_COMPLIANCE_SETTING_START,
  UPDATE_COMPLIANCE_SETTING_SUCCESS,
  UPDATE_COMPLIANCE_EMAILS_FAILURE,
  UPDATE_COMPLIANCE_EMAILS_START,
  UPDATE_COMPLIANCE_EMAILS_SUCCESS,
  FETCH_COMPLIANCE_NOTIFICATIONS_FAILURE,
  FETCH_COMPLIANCE_NOTIFICATIONS_START,
  FETCH_COMPLIANCE_NOTIFICATIONS_SUCCESS,
  UPDATE_COMPLIANCE_NOTIFICATIONS_FAILURE,
  UPDATE_COMPLIANCE_NOTIFICATIONS_START,
  UPDATE_COMPLIANCE_NOTIFICATIONS_SUCCESS,
} from './actions';

import { transformPerson } from '../../account/redux/account/person';
import { transformLocation } from '../../account/redux/account/location';
/* eslint-disable no-restricted-syntax */

const transformAccount = response => {
  return {
    id: response.id,
    businessName: response.account_business_name,
  };
};

const transformDocument = response => {
  return {
    documentName: response.background_assessment_document_name,
    documentFile:
      response.background_assessment_document_file.document_file_file,
    uploadedBy: response.background_assessment_document_uploaded_by,
    documentType: response.background_assessment_document_type,
  };
};

export const transformBackgroundAssessmentType = response => {
  return {
    id: response.id,
    type: response.background_assessment_type_type,
    description: response.background_assessment_type_description,
  };
};

export const transformBackgroundAssessmentTypeResult = response => {
  let backgroundAssessmentType =
    response.background_assessment_type_result_type;
  if (backgroundAssessmentType) {
    backgroundAssessmentType = transformBackgroundAssessmentType(
      backgroundAssessmentType
    );
  }

  return {
    id: response.id,
    backgroundAssessmentType,
    result: response.background_assessment_type_result_result,
    description: response.background_assessment_type_result_description,
    userActionRequired:
      response.background_assessment_type_result_user_action_required,
  };
};

export const transformBackgroundAssessment = response => {
  let backgroundAssessmentType = response.background_assessment_type;
  if (backgroundAssessmentType) {
    backgroundAssessmentType = transformBackgroundAssessmentType(
      backgroundAssessmentType
    );
  }

  let backgroundAssessmentTypeResult =
    response.background_assessment_type_result;
  if (backgroundAssessmentTypeResult) {
    backgroundAssessmentTypeResult = transformBackgroundAssessmentTypeResult(
      backgroundAssessmentTypeResult
    );
  }

  let account = response.background_assessment_account;
  if (account) {
    account = transformAccount(account);
  }

  let user = response.background_assessment_user;
  if (user) {
    user = transformPerson(user);
  }

  let location = response.background_assessment_location;
  if (location) {
    location = transformLocation(location);
  }

  let document = response.background_assessment_document;
  if (document) {
    document = transformDocument(document);
  }
  return {
    id: response.id,
    account,
    user,
    location,
    document,
    backgroundAssessmentType,
    backgroundAssessmentTypeResult,
    date: response.background_assessment_date,
    supplementalInformation:
      response.background_assessment_supplemental_information,
    resolved: response.background_assessment_resolved,
    acknowledged: response.background_assessment_acknowledged,
  };
};

export const transformDashboardSummaryCounts = d => ({
  accountId: d.background_assessment_account_id,
  businessName: d.background_assessment_account_business_name,
  failedAcknowledgedUnresolved: d.failed_acknowledged_unresolved,
  failedResolved: d.failed_resolved,
  failedUnresolved: d.failed_unresolved,
  needsReview: d.needs_review,
  needsReviewAcknowledgedUnresolved: d.needs_review_acknowledged_unresolved,
  needsReviewResolved: d.needs_review_resolved,
  needsReviewUnresolved: d.needs_review_unresolved,
  passed: d.passed,
  totalAlerts: d.total_alerts,
});

export const transformDashboardSummaryCountsTotals = d => ({
  needsReviewResolved: d.total_needs_review_resolved,
  needsReviewUnresolved: d.total_needs_review_unresolved,
  needsReviewAcknowledgedUnresolved:
    d.total_needs_review_acknowledged_unresolved,
  failedResolved: d.total_failed_resolved,
  failedUnresolved: d.total_failed_unresolved,
  failedAcknowledgedUnresolved: d.total_failed_acknowledged_unresolved,
  passed: d.total_passed,
});

export const transformDashboardData = response => ({
  counts: response.summary_counts.map(transformDashboardSummaryCounts),
  countsTotals: transformDashboardSummaryCountsTotals(
    response.summary_counts_totals
  ),
  unresolvedAcknowledged: response.unresolved_acknowledged.map(
    transformBackgroundAssessment
  ),
  unresolvedUnacknowledged: response.unresolved_unacknowledged.map(
    transformBackgroundAssessment
  ),
});

export const transformHistoryData = response => ({
  counts: response.summary_counts.map(transformDashboardSummaryCounts),
  countsTotals: transformDashboardSummaryCountsTotals(
    response.summary_counts_totals
  ),
  items: response.overview_results.map(transformBackgroundAssessment),
});

export const transformBackgroundAssessmentLogType = response => {
  return {
    id: response.id,
    type: response.background_assessment_log_type_type,
    description: response.background_assessment_log_type_description,
  };
};

export const transformBackgroundAssessmentLog = response => {
  let backgroundAssessment =
    response.background_assessment_log_background_assessment;
  if (backgroundAssessment) {
    backgroundAssessment = transformBackgroundAssessment(backgroundAssessment);
  }

  let backgroundAssessmentLogType = response.background_assessment_log_type;
  if (backgroundAssessmentLogType) {
    backgroundAssessmentLogType = transformBackgroundAssessmentLogType(
      backgroundAssessmentLogType
    );
  }

  return {
    id: response.id,
    backgroundAssessment,
    backgroundAssessmentLogType,
    date: response.background_assessment_log_date,
    details: response.background_assessment_log_details,
    originator: response.background_assessment_log_originator,
  };
};

const getTypeIdFromName = (backgroundAssessmentTypes, name) => {
  const typeObject = backgroundAssessmentTypes.find(
    ({ type }) => type === name
  );

  let type;
  if (typeObject === null || typeof typeObject === 'undefined') {
    type = 1;
  } else {
    type = typeObject.id;
  }

  return type;
};

const getLogTypeIdFromName = (backgroundAssessmentLogTypes, name) => {
  const logTypeObject = backgroundAssessmentLogTypes.find(
    ({ type }) => type === name
  );

  let logType;
  if (logTypeObject === null || typeof logTypeObject === 'undefined') {
    logType = 1;
  } else {
    logType = logTypeObject.id;
  }

  return logType;
};

const updateItem = (item, key, value, newItem) => {
  if (item && item[key] === value) {
    return newItem;
  }
  return item;
};

const updateItems = (list, key, value, newItem) => {
  return list.map(item => {
    return updateItem(item, key, value, newItem);
  });
};

const fetchAccountBackgroundAssessmentsReducer = createApiReducer(
  [
    FETCH_ACCOUNT_BACKGROUND_ASSESSMENTS_START,
    FETCH_ACCOUNT_BACKGROUND_ASSESSMENTS_SUCCESS,
    FETCH_ACCOUNT_BACKGROUND_ASSESSMENTS_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      accountBackgroundAssessments: payload.map(transformBackgroundAssessment),
    };
  },
  error => _.get(error, 'payload.response', 'unknown error')
);

const fetchBackgroundAssessmentReducer = createApiReducer(
  [
    FETCH_BACKGROUND_ASSESSMENT_START,
    FETCH_BACKGROUND_ASSESSMENT_SUCCESS,
    FETCH_BACKGROUND_ASSESSMENT_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      backgroundAssessment: transformBackgroundAssessment(payload),
    };
  },
  error => _.get(error, 'payload.response', 'unknown error')
);

const fetchComplianceDashboardDataReducer = createApiReducer(
  [
    FETCH_COMPLIANCE_DASHBOARD_DATA_START,
    FETCH_COMPLIANCE_DASHBOARD_DATA_SUCCESS,
    FETCH_COMPLIANCE_DASHBOARD_DATA_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      dashboardData: transformDashboardData(payload),
    };
  }
);

const fetchComplianceHistoryReducer = createApiReducer(
  [
    FETCH_COMPLIANCE_HISTORY_START,
    FETCH_COMPLIANCE_HISTORY_SUCCESS,
    FETCH_COMPLIANCE_HISTORY_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      historyData: transformHistoryData(payload),
    };
  }
);

const addBackgroundAssessmentReducer = createApiReducer(
  [
    ADD_BACKGROUND_ASSESSMENT_START,
    ADD_BACKGROUND_ASSESSMENT_SUCCESS,
    ADD_BACKGROUND_ASSESSMENT_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      backgroundAssessment: [
        transformBackgroundAssessment(payload),
        ...(state.data.backgroundAssessment || []),
      ],
    };
  },
  error => transformBackgroundAssessment(_.get(error, 'payload.response', {}))
);

const updateBackgroundAssessmentsReducer = createApiReducer(
  [
    UPDATE_BACKGROUND_ASSESSMENTS_START,
    UPDATE_BACKGROUND_ASSESSMENTS_SUCCESS,
    UPDATE_BACKGROUND_ASSESSMENTS_FAILURE,
  ],
  (payload, state) => {
    const newBackgroundAssessment = transformBackgroundAssessment(payload);

    return {
      ...state.data,
      fiBackgroundAssessments: updateItems(
        state.data.fiBackgroundAssessments,
        'id',
        newBackgroundAssessment.id,
        newBackgroundAssessment
      ),
    };
  },
  error => transformBackgroundAssessment(_.get(error, 'payload.response', {}))
);

const updateBackgroundAssessmentReducer = createApiReducer(
  [
    UPDATE_BACKGROUND_ASSESSMENT_START,
    UPDATE_BACKGROUND_ASSESSMENT_SUCCESS,
    UPDATE_BACKGROUND_ASSESSMENT_FAILURE,
  ],
  (payload, state) => {
    const newBackgroundAssessment = transformBackgroundAssessment(payload);
    const { historyData } = state.data;

    return {
      ...state.data,
      backgroundAssessment: updateItem(
        state.data.backgroundAssessment,
        'id',
        newBackgroundAssessment.id,
        newBackgroundAssessment
      ),
      // the overview is too slow to fetch again for one row, and its rows
      // are a short form of the assessment
      historyData: historyData && {
        ...historyData,
        items: historyData.items.map(item =>
          item.id === newBackgroundAssessment.id
            ? {
                ...item,
                acknowledged: newBackgroundAssessment.acknowledged,
                resolved: newBackgroundAssessment.resolved,
              }
            : item
        ),
      },
    };
  },
  error => {
    const response = _.get(error, 'payload.response') || {};
    return {
      ...transformBackgroundAssessment(response),
      detail: response.detail,
    };
  }
);

const fetchBackgroundAssessmentTypesReducer = createApiReducer(
  [
    FETCH_BACKGROUND_ASSESSMENT_TYPES_START,
    FETCH_BACKGROUND_ASSESSMENT_TYPES_SUCCESS,
    FETCH_BACKGROUND_ASSESSMENT_TYPES_FAILURE,
  ],
  (payload, state) => {
    const backgroundAssessmentTypeList = payload.map(
      transformBackgroundAssessmentType
    );
    const backgroundCheckBackgroundAssessmentType = getTypeIdFromName(
      backgroundAssessmentTypeList,
      'Background Check'
    );
    const financialCheckBackgroundAssessmentType = getTypeIdFromName(
      backgroundAssessmentTypeList,
      'Financial Check'
    );
    const businessLicenseCheckBackgroundAssessmentType = getTypeIdFromName(
      backgroundAssessmentTypeList,
      'Business License Check'
    );
    return {
      ...state.data,
      backgroundAssessmentTypeList,
      backgroundCheckBackgroundAssessmentType,
      financialCheckBackgroundAssessmentType,
      businessLicenseCheckBackgroundAssessmentType,
    };
  },
  error => _.get(error, 'payload')
);

const fetchBackgroundAssessmentTypeResultsReducer = createApiReducer(
  [
    FETCH_BACKGROUND_ASSESSMENT_TYPE_RESULTS_START,
    FETCH_BACKGROUND_ASSESSMENT_TYPE_RESULTS_SUCCESS,
    FETCH_BACKGROUND_ASSESSMENT_TYPE_RESULTS_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      backgroundAssessmentTypeResults: payload.map(
        transformBackgroundAssessmentTypeResult
      ),
    };
  },
  error => _.get(error, 'payload')
);

const fetchBackgroundAssessmentLogsReducer = createApiReducer(
  [
    FETCH_BACKGROUND_ASSESSMENT_LOGS_START,
    FETCH_BACKGROUND_ASSESSMENT_LOGS_SUCCESS,
    FETCH_BACKGROUND_ASSESSMENT_LOGS_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      backgroundAssessmentLogs: payload.map(transformBackgroundAssessmentLog),
    };
  },
  error => _.get(error, 'payload')
);

const addBackgroundAssessmentLogReducer = createApiReducer(
  [
    ADD_BACKGROUND_ASSESSMENT_LOG_START,
    ADD_BACKGROUND_ASSESSMENT_LOG_SUCCESS,
    ADD_BACKGROUND_ASSESSMENT_LOG_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      backgroundAssessmentLogs: [
        transformBackgroundAssessmentLog(payload),
        ...(state.data.backgroundAssessmentLogs || []),
      ],
    };
  },
  error => {
    const response = _.get(error, 'payload.response') || {};
    return {
      ...transformBackgroundAssessmentLog(response),
      detail: response.detail,
    };
  }
);

const fetchBackgroundAssessmentLogTypesReducer = createApiReducer(
  [
    FETCH_BACKGROUND_ASSESSMENT_LOG_TYPES_START,
    FETCH_BACKGROUND_ASSESSMENT_LOG_TYPES_SUCCESS,
    FETCH_BACKGROUND_ASSESSMENT_LOG_TYPES_FAILURE,
  ],
  (payload, state) => {
    const backgroundAssessmentLogTypeList = payload.map(
      transformBackgroundAssessmentLogType
    );
    const userCommentBackgroundAssessmentLogType = getLogTypeIdFromName(
      backgroundAssessmentLogTypeList,
      'User Comment'
    );
    const acknowledgedBackgroundAssessmentLogType = getLogTypeIdFromName(
      backgroundAssessmentLogTypeList,
      'Acknowledged'
    );
    const resolvedBackgroundAssessmentLogType = getLogTypeIdFromName(
      backgroundAssessmentLogTypeList,
      'Resolved'
    );
    return {
      ...state.data,
      backgroundAssessmentLogTypeList,
      userCommentBackgroundAssessmentLogType,
      acknowledgedBackgroundAssessmentLogType,
      resolvedBackgroundAssessmentLogType,
    };
  },
  error => _.get(error, 'payload')
);

const transformGratuity = response => {
  return {
    tolerance: response.compliance_task_settings_tolerance,
  };
};
const transformLicense = response => {
  return {
    timeframe: response.compliance_task_settings_timeframe,
  };
};

const transformComplianceEmail = response => {
  return {
    email: response.compliance_task_email_address,
    id: response.id,
    notifyOnFail: response.compliance_task_email_notify_failed,
    notifyOnPass: response.compliance_task_email_notify_passed,
    notifyOnReview: response.compliance_task_email_notify_review,
  };
};
const getEmailList = tasks => {
  const result = new Set();

  tasks.forEach(tsk => {
    const { taskEmailList } = tsk;
    for (const { email } of taskEmailList) {
      result.add(email);
    }
  });
  return [...result];
};
const transformComplianceSettings = response => {
  const gratuityTasks = [];
  const licenseTasks = [];
  const backgroundTasks = [];
  const businessTasks = [];
  const webTasks = [];
  const documentTasks = [];
  const processingTasks = [];
  const sarTasks = [];
  const changeTasks = [];

  response.forEach(task => {
    if (task.compliance_task_type === 'Gratuity') {
      gratuityTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
        settings: transformGratuity(task.compliance_task_settings),
      });
    }
    if (task.compliance_task_type === 'License') {
      licenseTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
        settings: transformLicense(task.compliance_task_settings),
      });
    }
    if (task.compliance_task_type === 'Background') {
      backgroundTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }

    if (task.compliance_task_type === 'Business License') {
      businessTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }

    if (task.compliance_task_type === 'Web Media') {
      webTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }

    if (task.compliance_task_type === 'Document') {
      documentTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }

    if (task.compliance_task_type === 'Processing') {
      processingTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }

    if (task.compliance_task_type === 'Sar') {
      sarTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }

    if (task.compliance_task_type === 'Change') {
      changeTasks.push({
        accountId: task.compliance_task_account,
        accountName: task.compliance_task_account_name,
        taskEmailList: task.compliance_task_email_list.map(email =>
          transformComplianceEmail(email)
        ),
        frequency: task.compliance_task_frequency,
        isActive: task.compliance_task_is_active,
        type: task.compliance_task_type,
        weight: task.compliance_task_weight,
        taskId: task.id,
      });
    }
  });

  return {
    Gratuity: {
      tasks: gratuityTasks,
      emailList: getEmailList(gratuityTasks),
    },
    License: {
      tasks: licenseTasks,
      emailList: getEmailList(licenseTasks),
    },
    Background: {
      tasks: backgroundTasks,
      emailList: getEmailList(backgroundTasks),
    },
    Business: {
      tasks: businessTasks,
      emailList: getEmailList(businessTasks),
    },
    Web: {
      tasks: webTasks,
      emailList: getEmailList(webTasks),
    },
    Document: {
      tasks: documentTasks,
      emailList: getEmailList(documentTasks),
    },
    Processing: {
      tasks: processingTasks,
      emailList: getEmailList(processingTasks),
    },
    Sar: {
      tasks: sarTasks,
      emailList: getEmailList(sarTasks),
    },
    Change: {
      tasks: changeTasks,
      emailList: getEmailList(changeTasks),
    },
  };
};

const transformComplianceNotifications = response => {
  return {
    id: response.compliance_task_email_settings.id,
    notifyOnPass:
      response.compliance_task_email_settings
        .compliance_task_email_notify_passed,
    notifyOnFail:
      response.compliance_task_email_settings
        .compliance_task_email_notify_failed,
    notifyOnReview:
      response.compliance_task_email_settings
        .compliance_task_email_notify_review,
    frequency: response.compliance_task_frequency,
    type: response.compliance_task_type,
    account: response.compliance_task_account_name,
  };
};

const updateComplianceSettingsReducer = createApiReducer(
  [
    UPDATE_COMPLIANCE_SETTINGS_START,
    UPDATE_COMPLIANCE_SETTINGS_SUCCESS,
    UPDATE_COMPLIANCE_SETTINGS_FAILURE,
  ],
  (payload, state) => {
    let { compliance_task_type: type } = payload[0];

    if (type.split(' ').length > 1) {
      type = type.split(' ')[0];
    }

    const copy = { ...state.data };
    copy[type] = transformComplianceSettings(payload)[type];

    return copy;
  },
  error => _.get(error, 'payload.response', 'unknown error')
);
const updateComplianceSettingReducer = createApiReducer(
  [
    UPDATE_COMPLIANCE_SETTING_START,
    UPDATE_COMPLIANCE_SETTING_SUCCESS,
    UPDATE_COMPLIANCE_SETTING_FAILURE,
  ],
  (payload, state) => {
    let type = payload.compliance_task_type;
    if (type.split(' ').length > 1) {
      type = type.split(' ')[0];
    }
    const copy = { ...state.data };
    const idx = copy[type].tasks.findIndex(obj => obj.taskId === payload.id);
    copy[type].tasks[idx] = transformComplianceSettings([payload])[
      type
    ].tasks[0];

    return copy;
  },
  error => _.get(error, 'payload.response', 'unknown error')
);

const updateComplianceEmailsReducer = createApiReducer(
  [
    UPDATE_COMPLIANCE_EMAILS_START,
    UPDATE_COMPLIANCE_EMAILS_SUCCESS,
    UPDATE_COMPLIANCE_EMAILS_FAILURE,
  ],
  (payload, state) => {
    let type = payload.compliance_task_type;
    if (type.split(' ').length > 1) {
      type = type.split(' ')[0];
    }
    const copy = { ...state.data };
    const idx = copy[type].tasks.findIndex(obj => obj.taskId === payload.id);
    copy[type].tasks[idx] = transformComplianceSettings([payload])[
      type
    ].tasks[0];

    return copy;
  },
  error => _.get(error, 'payload.response', 'unknown error')
);

const fetchComplianceSettings = createApiReducer(
  [
    FETCH_COMPLIANCE_SETTINGS_START,
    FETCH_COMPLIANCE_SETTINGS_SUCCESS,
    FETCH_COMPLIANCE_SETTINGS_FAILURE,
  ],
  transformComplianceSettings,
  error => _.get(error, 'payload.response', 'unknown error')
);

const fetchComplianceNotifications = createApiReducer(
  [
    FETCH_COMPLIANCE_NOTIFICATIONS_START,
    FETCH_COMPLIANCE_NOTIFICATIONS_SUCCESS,
    FETCH_COMPLIANCE_NOTIFICATIONS_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      complianceNotifications: payload.map(transformComplianceNotifications),
    };
  },
  error => _.get(error, 'payload.response', 'unknown error')
);
const updateComplianceNotifications = createApiReducer(
  [
    UPDATE_COMPLIANCE_NOTIFICATIONS_START,
    UPDATE_COMPLIANCE_NOTIFICATIONS_SUCCESS,
    UPDATE_COMPLIANCE_NOTIFICATIONS_FAILURE,
  ],
  (payload, state) => {
    const copy = [...state.data.complianceNotifications];

    payload.forEach(el => {
      const idx = copy.findIndex(x => x.id === el.id);
      copy[idx].notifyOnPass = el.compliance_task_email_notify_passed;
      copy[idx].notifyOnFail = el.compliance_task_email_notify_failed;
      copy[idx].notifyOnReview = el.compliance_task_email_notify_review;
    });

    return {
      ...state.data,
      complianceNotifications: copy,
    };
  },
  error => _.get(error, 'payload.response', 'unknown error')
);
export const backgroundAssessment = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchAccountBackgroundAssessmentsReducer,
  fetchBackgroundAssessmentReducer,
  fetchComplianceDashboardDataReducer,
  fetchComplianceHistoryReducer,
  addBackgroundAssessmentReducer,
  updateBackgroundAssessmentsReducer,
  updateBackgroundAssessmentReducer,
  fetchBackgroundAssessmentTypesReducer,
  fetchBackgroundAssessmentTypeResultsReducer
);

export const backgroundAssessmentLog = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchBackgroundAssessmentLogsReducer,
  addBackgroundAssessmentLogReducer,
  fetchBackgroundAssessmentLogTypesReducer
);
const complianceSettings = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchComplianceSettings,
  updateComplianceSettingsReducer,
  updateComplianceSettingReducer,
  updateComplianceEmailsReducer
);

const complianceNotifications = chainReducers(
  INITIAL_API_STATE_ITEMS,
  fetchComplianceNotifications,
  updateComplianceNotifications
);

export const reducer = combineReducers({
  backgroundAssessment,
  backgroundAssessmentLog,
  complianceSettings,
  complianceNotifications,
});
